import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Flame, Calendar, GitCompareArrows, ImageDown, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { fetchHeatmap } from '../lib/api';
import PageSkeleton from './nav/PageSkeleton';
import { downloadHeatmapPng } from '../lib/heatmapImage';
import Breadcrumb from './nav/Breadcrumb';
import EmptyState from './nav/EmptyState';
import HabitsHeatmap90 from './nav/HabitsHeatmap90';

const MONTH_LABELS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const GIORNI_SHORT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

// Mappa livello (0-4) → tinta verde. Palette identica al PNG renderer per coerenza.
const LEVELS = [
  { bg: 'transparent', border: 'var(--qp-grid-empty, var(--qp-border))' }, // 0
  { bg: 'rgb(var(--qp-success-rgb) / 0.22)', border: 'transparent' }, // 1
  { bg: 'rgb(var(--qp-success-rgb) / 0.45)', border: 'transparent' }, // 2
  { bg: 'rgb(var(--qp-success-rgb) / 0.70)', border: 'transparent' }, // 3
  { bg: 'rgb(var(--qp-success-rgb) / 0.95)', border: 'transparent' }, // 4
];

function todayYear() { return new Date().getFullYear(); }

export default function PaginaHeatmap() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialYear = parseInt(searchParams.get('anno') || todayYear(), 10);
  const initialCompare = searchParams.get('cmp') ? parseInt(searchParams.get('cmp'), 10) : null;

  const [year, setYear] = useState(initialYear);
  const [compareYear, setCompareYear] = useState(initialCompare);
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchHeatmap(year)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'errore'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  useEffect(() => {
    if (!compareYear) { setCompareData(null); return undefined; }
    let cancelled = false;
    fetchHeatmap(compareYear)
      .then((d) => { if (!cancelled) setCompareData(d); })
      .catch(() => { if (!cancelled) setCompareData(null); });
    return () => { cancelled = true; };
  }, [compareYear]);

  const onChangeYear = (next) => {
    setYear(next);
    setSearchParams((p) => { p.set('anno', String(next)); return p; }, { replace: true });
  };

  const toggleCompare = () => {
    if (compareYear) {
      setCompareYear(null);
      setSearchParams((p) => { p.delete('cmp'); return p; }, { replace: true });
    } else {
      const cmp = year - 1;
      setCompareYear(cmp);
      setSearchParams((p) => { p.set('cmp', String(cmp)); return p; }, { replace: true });
    }
  };

  const handleExportPng = () => {
    if (!data) return;
    try {
      const dark = document.documentElement.classList.contains('dark');
      downloadHeatmapPng({ heatmap: data, compare: compareData || null, dark });
      toast.success('PNG scaricato');
    } catch (e) {
      toast.error(`Export PNG fallito · ${e?.message || 'errore'}`);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/"
          backLabel="Dashboard"
          items={[{ label: 'Dashboard', to: '/' }, { label: 'Heatmap annuale' }]}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20">
        <header className="border-b border-line pb-6 mb-8" data-testid="heatmap-header">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="mono-label">Esecuzione MIT</span>
              <h1 className="font-display tracking-tight text-4xl md:text-5xl lg:text-6xl mt-2">
                Heatmap {year}
              </h1>
              <p className="font-body text-base text-soft mt-3 leading-relaxed max-w-2xl">
                Una cella per giorno. Più intenso il verde, più MIT hai chiuso.
                Clicca un giorno per aprirlo.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Year nav */}
              <button
                type="button"
                onClick={() => onChangeYear(year - 1)}
                className="p-2 border border-line text-soft hover:border-ink hover:text-ink transition-colors"
                aria-label="Anno precedente"
                data-testid="heatmap-prev-year"
              >
                <ChevronLeft size={16} strokeWidth={1.5} />
              </button>
              <span className="font-mono text-sm tabular-nums px-3 py-1.5 border border-ink min-w-[80px] text-center" data-testid="heatmap-year-label">
                {year}
              </span>
              <button
                type="button"
                onClick={() => onChangeYear(year + 1)}
                className="p-2 border border-line text-soft hover:border-ink hover:text-ink transition-colors"
                aria-label="Anno successivo"
                data-testid="heatmap-next-year"
              >
                <ChevronRight size={16} strokeWidth={1.5} />
              </button>

              {/* Compare toggle */}
              <button
                type="button"
                onClick={toggleCompare}
                className={
                  'inline-flex items-center gap-1.5 px-3 py-2 border font-mono text-xs uppercase tracking-[0.18em] transition-colors ' +
                  (compareYear
                    ? 'border-ink bg-ink text-canvas'
                    : 'border-line text-soft hover:border-ink hover:text-ink')
                }
                data-testid="heatmap-compare-btn"
                aria-pressed={!!compareYear}
              >
                <GitCompareArrows size={13} strokeWidth={1.5} />
                {compareYear ? `vs ${compareYear}` : 'Confronta'}
              </button>

              {/* PNG export */}
              <button
                type="button"
                onClick={handleExportPng}
                disabled={!data}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-line text-soft hover:border-ink hover:text-ink font-mono text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-50"
                data-testid="heatmap-export-png-btn"
              >
                <ImageDown size={13} strokeWidth={1.5} />
                PNG
              </button>
            </div>
          </div>

          {compareYear && (
            <div className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-soft" data-testid="heatmap-compare-bar">
              <span>Confronto:</span>
              <button
                type="button"
                onClick={() => setCompareYear(compareYear - 1)}
                className="p-1 border border-line hover:border-ink hover:text-ink"
                aria-label="Anno confronto precedente"
                data-testid="heatmap-compare-prev"
              >
                <ChevronLeft size={12} strokeWidth={1.5} />
              </button>
              <span className="px-2 py-1 border border-line tabular-nums" data-testid="heatmap-compare-year">{compareYear}</span>
              <button
                type="button"
                onClick={() => setCompareYear(compareYear + 1)}
                className="p-1 border border-line hover:border-ink hover:text-ink"
                aria-label="Anno confronto successivo"
                data-testid="heatmap-compare-next"
              >
                <ChevronRight size={12} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={toggleCompare}
                className="p-1 text-mute hover:text-accent"
                aria-label="Chiudi confronto"
                data-testid="heatmap-compare-close"
              >
                <X size={12} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </header>

        {loading && (
          <PageSkeleton variant="calendar" inline testid="heatmap-loading" />
        )}
        {error && (
          <EmptyState icon={Calendar} title="Errore caricamento heatmap" description={error} />
        )}

        {data && !loading && !error && (
          <>
            <HeatmapBlock data={data} navigate={navigate} testidPrefix="" />

            {compareYear && compareData && (
              <div className="mt-12 pt-10 border-t border-line" data-testid="heatmap-compare-block">
                <div className="mb-4 flex items-baseline gap-3">
                  <h2 className="font-display tracking-tight text-2xl">Confronto · {compareYear}</h2>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                    {data.totals.days_with_mit - (compareData.totals.days_with_mit || 0) >= 0 ? '+' : ''}
                    {data.totals.days_with_mit - (compareData.totals.days_with_mit || 0)} gg vs {year}
                  </span>
                </div>
                <HeatmapBlock data={compareData} navigate={navigate} testidPrefix="cmp-" />
              </div>
            )}

            <p className="font-body text-[12px] text-mute mt-6">
              Suggerimento: usa &quot;Confronta&quot; per vedere l&apos;anno precedente sotto, &quot;PNG&quot; per scaricare un&apos;immagine condivisibile.
            </p>
          </>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to={`/recap/${year}`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink hover:bg-ink hover:text-canvas font-mono text-xs uppercase tracking-[0.18em] transition-colors"
            data-testid="heatmap-open-recap"
          >
            <Sparkles size={13} strokeWidth={1.5} /> Year in Review {year}
          </Link>
          <Link
            to="/calendario"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-soft hover:text-ink transition-colors inline-flex items-center gap-1"
          >
            <Calendar size={12} strokeWidth={1.5} /> Apri calendario mensile
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Blocco riusabile: KPI + griglia + legenda. */
function HeatmapBlock({ data, navigate, testidPrefix }) {
  const monthLabels = useMemo(() => {
    if (!data?.weeks) return [];
    const out = [];
    let lastMonth = -1;
    data.weeks.forEach((w, idx) => {
      const ref = w.find((d) => d.in_year) || w[0];
      if (!ref) { out.push(null); return; }
      const m = new Date(ref.date).getMonth();
      if (m !== lastMonth) {
        lastMonth = m;
        out.push({ col: idx, label: MONTH_LABELS_IT[m] });
      } else {
        out.push(null);
      }
    });
    return out;
  }, [data]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid={`${testidPrefix}heatmap-stats`}>
        <Stat label="Giorni con MIT" value={data.totals.days_with_mit} suffix={`/ ${data.totals.days_tracked}`} />
        <Stat label="Tasso" value={`${Math.round((data.totals.ratio || 0) * 100)}%`} />
        <Stat label="Streak attuale" value={data.totals.streak_current} suffix="gg" icon={Flame} />
        <Stat label="Streak record" value={data.totals.streak_best} suffix="gg" />
      </div>

      <div className="overflow-x-auto pb-4" data-testid={`${testidPrefix}heatmap-grid-wrapper`}>
        <div className="inline-block min-w-full">
          <div className="flex gap-[3px] mb-2 pl-[28px]" data-testid={`${testidPrefix}heatmap-months`}>
            {monthLabels.map((m, i) => (
              <div key={`m-${i}-${m?.label || 'gap'}`} className="w-[14px] shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                {m ? m.label : ''}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <div className="flex flex-col gap-[3px] pr-1">
              {GIORNI_SHORT.map((g, i) => (
                <div key={`d-${i}-${g}`} className="h-[14px] w-[18px] font-mono text-[9px] uppercase tracking-[0.18em] text-mute flex items-center">
                  {i % 2 === 0 ? g : ''}
                </div>
              ))}
            </div>
            <div className="flex gap-[3px]" data-testid={`${testidPrefix}heatmap-grid`}>
              {data.weeks.map((week, wi) => (
                <div key={`w-${wi}`} className="flex flex-col gap-[3px]">
                  {week.map((day) => {
                    const style = LEVELS[day.level] || LEVELS[0];
                    const muted = !day.in_year || day.is_future;
                    const title = day.in_year
                      ? `${day.date} — ${day.done}/${day.total} MIT`
                      : day.date;
                    if (muted || day.total === 0) {
                      return (
                        <div
                          key={day.date}
                          title={title}
                          className={
                            'w-[14px] h-[14px] border ' +
                            (day.is_today ? 'ring-1 ring-accent ring-offset-1 ring-offset-canvas ' : '') +
                            (muted ? 'opacity-40 ' : '')
                          }
                          style={{ backgroundColor: style.bg, borderColor: style.border }}
                          data-testid={`${testidPrefix}heatmap-cell-${day.date}`}
                          data-level={day.level}
                        />
                      );
                    }
                    return (
                      <button
                        type="button"
                        key={day.date}
                        onClick={() => navigate(`/periodo/giorno/${day.date}`)}
                        title={title}
                        className={
                          'w-[14px] h-[14px] border cursor-pointer transition-transform hover:scale-125 ' +
                          (day.is_today ? 'ring-1 ring-accent ring-offset-1 ring-offset-canvas ' : '')
                        }
                        style={{ backgroundColor: style.bg, borderColor: style.border }}
                        data-testid={`${testidPrefix}heatmap-cell-${day.date}`}
                        data-level={day.level}
                        aria-label={`${day.date}: ${day.done} su ${day.total} MIT`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 pl-[28px] font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
            <span>Meno</span>
            {LEVELS.map((l, i) => (
              <div key={`lvl-${i}-${l.bg}`} className="w-[14px] h-[14px] border" style={{ backgroundColor: l.bg, borderColor: l.border }} />
            ))}
            <span>Più</span>
          </div>
        </div>

        {/* Heatmap delle abitudini ricorrenti (ultimi 90gg) */}
        <HabitsHeatmap90 />
      </div>
    </>
  );
}

function Stat({ label, value, suffix, icon: Icon }) {
  return (
    <div className="border border-line p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute flex items-center gap-1">
        {Icon ? <Icon size={11} strokeWidth={1.5} /> : null}
        {label}
      </div>
      <div className="mt-2 font-display text-3xl tracking-tight tabular-nums">
        {value} {suffix ? <span className="text-base text-mute font-body">{suffix}</span> : null}
      </div>
    </div>
  );
}
